import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { resetPassword } from '../../services/firebase/auth';
import { logActivity } from '../../services/firebase/firestore';
import { ActionType, EntityType } from '../../types/deal.types';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required')
});

type FormData = yup.InferType<typeof schema>;

export const PasswordReset: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    resolver: yupResolver(schema)
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setError(null);

    try {
      await resetPassword(data.email);
      
      await logActivity(
        'anonymous',
        data.email,
        ActionType.PASSWORD_RESET,
        EntityType.USER,
        'anonymous',
        data.email,
        undefined,
        'success'
      );

      setSuccess(true);
    } catch (err: any) {
      setError(err.message);
      
      await logActivity(
        'anonymous',
        data.email,
        ActionType.PASSWORD_RESET,
        EntityType.USER,
        'anonymous',
        data.email,
        undefined,
        'failed',
        err.message
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10 p-4">
      <div className="w-full max-w-md">
        <div className="glass-card p-8 space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-full mb-4">
              <span className="text-white text-2xl font-bold">PC</span>
            </div>
            <h1 className="text-2xl font-bold text-neutral-900">Reset Password</h1>
            <p className="text-neutral-600 mt-2">
              {success 
                ? 'Check your email for reset instructions' 
                : 'Enter your email to reset your password'}
            </p>
          </div>

          {success ? (
            <div className="space-y-4">
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-success">Email sent successfully!</p>
                  <p className="text-sm text-neutral-600 mt-1">
                    We've sent password reset instructions to your email address.
                  </p>
                </div>
              </div>
              
              <Link
                to="/login"
                className="primary-button w-full py-3 text-center inline-block"
              >
                Back to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                  <input
                    {...register('email')}
                    type="email"
                    className="input-field pl-10"
                    placeholder="Enter your email"
                    disabled={isLoading}
                  />
                </div>
                {errors.email && (
                  <p className="text-error text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              {error && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-3">
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="primary-button w-full py-3"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  </div>
                ) : (
                  'Send Reset Email'
                )}
              </button>

              <Link
                to="/login"
                className="flex items-center justify-center space-x-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Login</span>
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};